import {
  createInvitationMutation,
  listInvitationsOptions,
  listInvitationsQueryKey,
  listMembersOptions,
  listMembersQueryKey,
  listRolesOptions,
  removeMemberMutation,
  revokeInvitationMutation,
  updateMemberMutation,
} from "@payezy/api-client/public/query";
import type { Member } from "@payezy/api-client/public";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuItem,
  PageHeader,
  Select,
  Table,
  TD,
  TH,
  THead,
  TR,
  formatDate,
  toast,
} from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, UserPlus } from "lucide-react";
import { useState } from "react";
import { problemMessage } from "../lib/api";
import { useMe, useOrg } from "../lib/org";

export function TeamPage() {
  const org = useOrg();
  const me = useMe();
  const qc = useQueryClient();
  const path = { orgId: org.org_id };
  const members = useQuery(listMembersOptions({ path }));
  const invitations = useQuery(listInvitationsOptions({ path }));
  const roles = useQuery(listRolesOptions({ path }));
  const canManage = org.can("members:manage");
  const canAssign = org.can("roles:assign");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: listMembersQueryKey({ path }) });
    void qc.invalidateQueries({ queryKey: listInvitationsQueryKey({ path }) });
  };
  const onError = (e: unknown) => toast.error(problemMessage(e));
  const update = useMutation({ ...updateMemberMutation(), onSuccess: () => { toast.success("Member updated"); refresh(); }, onError });
  const remove = useMutation({ ...removeMemberMutation(), onSuccess: () => { toast.success("Member removed"); refresh(); }, onError });
  const revoke = useMutation({ ...revokeInvitationMutation(), onSuccess: () => { toast.success("Invitation revoked"); refresh(); }, onError });

  const roleName = (key: string) => roles.data?.find((r) => r.key === key)?.name ?? key;

  return (
    <>
      <PageHeader
        title="Team"
        description="Invite teammates and control what they can do."
        actions={canManage && org.org_type === "business" && <InviteDialog orgId={org.org_id} onDone={refresh} />}
      />
      <Card>
        <CardHeader title="Members" />
        <Table>
          <THead>
            <tr>
              <TH>Name</TH>
              <TH>Role</TH>
              <TH>Status</TH>
              <TH>Joined</TH>
              <TH className="w-10" />
            </tr>
          </THead>
          <tbody>
            {members.data?.map((m: Member) => (
              <TR key={m.user_id}>
                <TD>
                  <div className="flex items-center gap-3">
                    <Avatar name={`${m.first_name} ${m.last_name}`} />
                    <div>
                      <p className="font-medium">{m.first_name} {m.last_name} {m.user_id === me.user.id && <span className="text-ink-400">(you)</span>}</p>
                      <p className="text-xs text-ink-500">{m.email}</p>
                    </div>
                  </div>
                </TD>
                <TD>
                  {canAssign && m.user_id !== me.user.id ? (
                    <Select
                      className="h-8 w-44"
                      value={m.role_key}
                      onChange={(e) => update.mutate({ path: { ...path, userId: m.user_id }, body: { role_key: e.target.value } })}
                    >
                      {roles.data?.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </Select>
                  ) : (
                    roleName(m.role_key)
                  )}
                </TD>
                <TD><Badge tone={m.status === "active" ? "success" : "neutral"}>{m.status}</Badge></TD>
                <TD className="text-ink-500">{formatDate(m.joined_at)}</TD>
                <TD>
                  {(canManage || m.user_id === me.user.id) && (
                    <Menu trigger={<Button variant="ghost" size="icon"><MoreHorizontal className="size-4" /></Button>}>
                      {canManage && m.user_id !== me.user.id && (
                        <MenuItem onSelect={() => update.mutate({ path: { ...path, userId: m.user_id }, body: { status: m.status === "active" ? "disabled" : "active" } })}>
                          {m.status === "active" ? "Disable access" : "Re-enable access"}
                        </MenuItem>
                      )}
                      <MenuItem
                        className="text-red-600"
                        onSelect={() => {
                          if (confirm(m.user_id === me.user.id ? "Leave this organization?" : `Remove ${m.email}?`)) {
                            remove.mutate({ path: { ...path, userId: m.user_id } });
                          }
                        }}
                      >
                        {m.user_id === me.user.id ? "Leave organization" : "Remove from team"}
                      </MenuItem>
                    </Menu>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>

      {org.org_type === "business" && (
        <Card className="mt-6">
          <CardHeader title="Pending invitations" />
          {invitations.data?.length ? (
            <Table>
              <THead>
                <tr><TH>Email</TH><TH>Role</TH><TH>Expires</TH><TH className="w-24" /></tr>
              </THead>
              <tbody>
                {invitations.data.map((i) => (
                  <TR key={i.id}>
                    <TD>{i.email}</TD>
                    <TD>{roleName(i.role_key)}</TD>
                    <TD className="text-ink-500">{i.status === "expired" ? <Badge tone="warning">Expired</Badge> : formatDate(i.expires_at)}</TD>
                    <TD>{canManage && <Button size="sm" variant="ghost" onClick={() => revoke.mutate({ path: { ...path, invitationId: i.id } })}>Revoke</Button>}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState title="No pending invitations" />
          )}
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader title="Roles" description="What each role can do." />
        <Table>
          <THead><tr><TH>Role</TH><TH>Permissions</TH></tr></THead>
          <tbody>
            {roles.data?.map((r) => (
              <TR key={r.key}>
                <TD className="align-top">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-ink-500">{r.description}</p>
                </TD>
                <TD>
                  <div className="flex flex-wrap gap-1">{r.permissions.map((p) => <Badge key={p}>{p}</Badge>)}</div>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function InviteDialog({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const roles = useQuery(listRolesOptions({ path: { orgId } }));
  const invite = useMutation({
    ...createInvitationMutation(),
    onSuccess: () => {
      toast.success(`Invitation sent to ${email}`);
      setOpen(false);
      setEmail("");
      onDone();
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="Invite a teammate"
      description="They'll get an email with a link to join."
      trigger={<Button><UserPlus className="size-4" /> Invite</Button>}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate({ path: { orgId }, body: { email, role_key: role } });
        }}
      >
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
        </Field>
        <Field label="Role" hint={roles.data?.find((r) => r.key === role)?.description}>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.data?.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </Select>
        </Field>
        {invite.isError && <Alert tone="danger">{problemMessage(invite.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="submit" loading={invite.isPending}>Send invitation</Button>
        </div>
      </form>
    </Dialog>
  );
}
